import { MDSDropdownItem } from "../../lists/dropdown-list-item";
import { ComboboxState, ComboboxAction } from "./combobox.types";

export function comboboxReducer<T extends MDSDropdownItem>(
  state: ComboboxState<T>,
  action: ComboboxAction<T>
): ComboboxState<T> {
  switch (action.type) {
    case "QUERY_CHANGED":
      return {
        ...state,
        query: action.payload,
        // Only clear highlight when query actually changes (user types new content)
        highlightedItemId: state.query !== action.payload ? null : state.highlightedItemId,
      };

    case "SEARCH_STARTED":
      return {
        ...state,
        isLoading: false,
        isOpen: true,
        highlightedItemId: null,
      };

    case "SEARCH_COMPLETED": {
      const highlightedItemId = action.payload.length > 0 ? action.payload[0].id : null;
      return {
        ...state,
        data: action.payload,
        hasSearched: true,
        highlightedItemId, // Auto-highlight first item if results exist
      };
    }

    case "SEARCH_FAILED":
      return {
        ...state,
        hasSearched: true,
        highlightedItemId: null,
      };

    case "LOADING_STARTED":
      return {
        ...state,
        hasSearched: false,
        isLoading: true,
        data: [] as T[],
      };

    case "LOADING_STOPPED":
      return {
        ...state,
        isLoading: false,
      };

    case "POPOVER_OPENED":
      return {
        ...state,
        isOpen: true,
      };

    case "POPOVER_CLOSED":
      return {
        ...state,
        isOpen: false,
        highlightedItemId: null,
      };

    case "EMPTY_STATE_CHANGED":
      return {
        ...state,
        isEmpty: action.payload,
      };

    case "HIGHLIGHT_CHANGED":
      return {
        ...state,
        highlightedItemId: action.payload,
      };

    case "HIGHLIGHT_NEXT": {
      if (state.data.length === 0) return state;

      const currentIndex = state.highlightedItemId
        ? state.data.findIndex(item => item.id === state.highlightedItemId)
        : -1;

      const nextIndex = currentIndex < state.data.length - 1 ? currentIndex + 1 : 0;

      return {
        ...state,
        highlightedItemId: state.data[nextIndex].id,
      };
    }

    case "HIGHLIGHT_PREVIOUS": {
      if (state.data.length === 0) return state;

      const currentIndex = state.highlightedItemId
        ? state.data.findIndex(item => item.id === state.highlightedItemId)
        : -1;

      const prevIndex = currentIndex > 0 ? currentIndex - 1 : state.data.length - 1;

      return {
        ...state,
        highlightedItemId: state.data[prevIndex].id,
      };
    }

    case "RESET":
      return {
        ...state,
        isOpen: false,
        isLoading: false,
        hasSearched: false,
        data: [] as T[],
        query: "",
        highlightedItemId: null,
      };

    case "POPOVER_MENTION_MODE_CHANGED":
      return {
        ...state,
        popoverMentionMode: action.payload,
      };

    default:
      return state;
  }
}
